import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { MapPin, CalendarCheck, Check, Tent, Compass, ArrowRight, Phone } from 'lucide-react';
import SEO, { generateFAQSchema, generateBreadcrumbSchema, generateBusinessSchema } from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { getLocation, locations } from '../data/locations';
import { guides } from '../data/guides';
import { useAPI } from '../hooks/useAPI';
import { publicAPI } from '../services/api';

const LocationPage = () => {
  const { slug } = useParams();
  const loc = getLocation(slug);
  const { data: motorhomes } = useAPI(() => publicAPI.getMotorhomes(), []);

  if (!loc) return <Navigate to="/motorhome-hire/suffolk" replace />;

  const path = `/motorhome-hire/${loc.slug}`;
  const others = locations.filter((l) => l.slug !== loc.slug);
  const faqs = loc.faqs.map((f) => ({ question: f.q, answer: f.a }));
  const schema = [
    generateBusinessSchema(),
    generateFAQSchema(faqs),
    generateBreadcrumbSchema([{ name: 'Motorhome Hire Areas', path: '/motorhome-hire/suffolk' }, { name: loc.name, path }]),
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid={`location-page-${loc.slug}`}>
      <SEO title={loc.title} rawTitle description={loc.metaDescription} url={path} structuredData={schema}
        keywords={`motorhome hire ${loc.name}, campervan hire ${loc.name}, motorhome rental ${loc.name}, ${loc.county || loc.name} motorhome hire`} />

      <section className="bg-slate-900 text-white py-14">
        <div className="max-w-7xl mx-auto px-4">
          <div className="mb-6"><Breadcrumbs light items={[{ name: 'Areas We Cover' }, { name: loc.name }]} /></div>
          <Badge className="mb-4 bg-orange-500 hover:bg-orange-600"><MapPin className="h-3.5 w-3.5 mr-1" /> {loc.distance}</Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">{loc.h1}</h1>
          <p className="text-lg md:text-xl text-slate-300 max-w-3xl mb-8">{loc.tagline}</p>
          <div className="flex flex-wrap gap-3">
            <Button asChild size="lg" className="bg-green-600 hover:bg-green-700 font-bold">
              <Link to="/#book-online" data-testid="location-book-btn"><CalendarCheck className="mr-2 h-5 w-5" /> Check Prices & Book Online</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="bg-white/10 text-white border-white hover:bg-white hover:text-slate-900">
              <a href="tel:07527006966"><Phone className="mr-2 h-5 w-5" /> 07527 006966</a>
            </Button>
          </div>
        </div>
      </section>

      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-[1.4fr_1fr] gap-12">
          <article className="prose prose-slate max-w-none">
            {loc.intro.map((p, i) => <p key={i} className="text-slate-700 text-base md:text-lg leading-relaxed mb-5">{p}</p>)}
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-10 mb-4">What's included with every hire</h2>
            <ul className="grid sm:grid-cols-2 gap-3 not-prose">
              {['Fully comprehensive insurance', 'Unlimited mileage', 'Full gas bottle & kitchen equipment', 'Free car parking while you\'re away', 'Pets welcome (from £20)', 'Collect 3pm · Return 11am', 'Thorough, unhurried handover', 'Camping & Caravanning Club discounts'].map((t) => (
                <li key={t} className="flex items-center gap-2 text-slate-700"><Check className="h-5 w-5 text-green-600 flex-shrink-0" />{t}</li>
              ))}
            </ul>
          </article>
          <aside className="space-y-6">
            <Card className="border-green-200 bg-green-50">
              <CardContent className="p-6">
                <h2 className="text-lg md:text-xl font-bold text-slate-900 mb-2">Prices from £120 per night</h2>
                <p className="text-slate-700 text-sm mb-4">Live availability for every motorhome - pick your dates and book online in minutes.</p>
                <Button asChild className="w-full bg-green-600 hover:bg-green-700 font-bold"><Link to="/#book-online">Check Availability</Link></Button>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <h2 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2"><Tent className="h-5 w-5 text-blue-600" /> Recommended campsites</h2>
                <ul className="space-y-2 text-sm text-slate-700">{loc.campsites.map((c) => <li key={c} className="flex gap-2"><Check className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />{c}</li>)}</ul>
              </CardContent>
            </Card>
          </aside>
        </div>
      </section>

      <section className="py-14 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8 flex items-center gap-2"><Compass className="h-7 w-7 text-orange-500" /> Places to go from {loc.name}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {loc.highlights.map((h) => (
              <Card key={h.name} className="hover:shadow-md transition-shadow"><CardContent className="p-6"><h3 className="font-bold text-slate-900 mb-1">{h.name}</h3><p className="text-sm text-slate-600">{h.text}</p></CardContent></Card>
            ))}
          </div>
        </div>
      </section>

      {motorhomes?.length > 0 && (
        <section className="py-14 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8">Motorhomes available for hire in {loc.name}</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {motorhomes.map((m) => (
                <Card key={m.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                  <img src={m.images?.[0]} alt={`${m.name} motorhome for hire near ${loc.name}`} className="h-44 w-full object-cover" loading="lazy" />
                  <CardContent className="p-4">
                    <h3 className="font-bold text-slate-900 text-sm mb-1">{m.name}</h3>
                    <p className="text-xs text-slate-500 mb-3">{m.berth} berth · {m.seatBelts} belts · {m.type} · from £{m.pricePerNight}/night</p>
                    <Button asChild size="sm" className="w-full bg-green-600 hover:bg-green-700"><Link to={`/motorhomes/${m.slug}?book=1`}>Check Availability</Link></Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-14 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">Motorhome hire {loc.name} - your questions answered</h2>
          <div className="space-y-4">{loc.faqs.map((f) => <div key={f.q} className="bg-white rounded-xl p-6 border"><h3 className="font-semibold text-slate-900 mb-2">{f.q}</h3><p className="text-slate-600 text-sm">{f.a}</p></div>)}</div>
        </div>
      </section>

      <section className="py-12 bg-white border-t">
        <div className="max-w-7xl mx-auto px-4 grid md:grid-cols-2 gap-10">
          <div>
            <h2 className="text-xl font-bold text-slate-900 mb-4">Other areas we cover</h2>
            <div className="flex flex-wrap gap-2">{others.map((l) => <Link key={l.slug} to={`/motorhome-hire/${l.slug}`} className="text-sm bg-slate-100 hover:bg-blue-600 hover:text-white px-3 py-1.5 rounded-full transition-colors">Motorhome hire {l.name}</Link>)}</div>
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900 mb-4">Trip ideas & guides</h2>
            <ul className="space-y-2">{guides.slice(0, 4).map((g) => <li key={g.slug}><Link to={`/guides/${g.slug}`} className="text-sm text-blue-700 hover:underline flex items-center gap-1"><ArrowRight className="h-3.5 w-3.5" />{g.title}</Link></li>)}</ul>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LocationPage;
